/**
 * ONE TOOLTIP FOR THE WHOLE PORTAL (Gerónimo, 1 Oct 2026: «los hovers son
 * feos, negros… quiero un hover chulo para todo, y que funcione bien»).
 *
 * Two kinds of hover text lived side by side: `data-tip`, a black bubble drawn
 * with CSS inside its own element — so any card that clips its overflow (a
 * board column, a panel) cut it in half — and the native `title`, the
 * browser's grey box after a second's wait. Both are read here instead and
 * drawn ONCE, in a light card on top of everything, placed against the
 * window, never inside the element: nothing can clip it.
 *
 *  · `data-tip="…"` — the text; `data-tip-h="…"` — an optional bold heading;
 *    `data-tip-side="right"` — beside the element (the sidebar) instead of
 *    above it; the class `tip-down` — below it.
 *  · `title="…"` — moved to `data-tip` the first time the pointer arrives, so
 *    the browser's own box never shows next to this one. When the element
 *    has no words of its own, the title becomes its aria-label, so a screen
 *    reader still hears it.
 *
 * Mouse and pen only: a finger is not a hover, and a tap must stay a tap.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Tip = { el: HTMLElement; text: string; head: string; side: 'top' | 'bottom' | 'right' };

const SKIP = 'iframe, input, textarea, select, option, [contenteditable="true"]';

function adopt(el: HTMLElement): void {
  const t = el.getAttribute('title');
  if (!t) return;
  if (!el.getAttribute('data-tip')) el.setAttribute('data-tip', t);
  if (!el.getAttribute('aria-label') && !(el.textContent || '').trim()) el.setAttribute('aria-label', t);
  el.removeAttribute('title');
}

function tipOf(el: HTMLElement): Tip | null {
  const text = (el.getAttribute('data-tip') || '').trim();
  const head = (el.getAttribute('data-tip-h') || '').trim();
  if (!text && !head) return null;
  const side = el.getAttribute('data-tip-side') === 'right' ? 'right' : el.classList.contains('tip-down') ? 'bottom' : 'top';
  return { el, text: text === head ? '' : text, head, side };
}

export function TipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number; side: Tip['side'] } | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | null>(null);
  /* The element under the pointer — pending or shown — read by the leave
     handler without going through React state. */
  const target = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const clear = () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };
    const hide = () => { clear(); target.current = null; setTip(null); setPos(null); };
    const over = (e: PointerEvent) => {
      /* A finger is not a hover: a tap must stay a tap. */
      if (e.pointerType === 'touch') return;
      const t = e.target as HTMLElement | null;
      if (!t || !t.closest) return;
      const el = t.closest<HTMLElement>('[data-tip], [data-tip-h], [title]');
      if (!el || el.closest(SKIP) || el.closest('.tipx')) return;
      if (el.hasAttribute('title')) adopt(el);
      const next = tipOf(el);
      if (!next) return;
      if (target.current === el) return;
      clear();
      setTip(null);
      target.current = el;
      timer.current = window.setTimeout(() => { if (target.current === el) setTip(next); }, 260);
    };
    const out = (e: PointerEvent) => {
      const cur = target.current;
      if (!cur) return;
      const to = e.relatedTarget as Node | null;
      if (to && cur.contains(to)) return;
      if (cur.contains(e.target as Node)) hide();
    };
    const focus = (e: FocusEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-tip], [data-tip-h]');
      if (!el || !el.matches(':focus-visible')) return;
      const next = tipOf(el);
      if (next) setTip(next);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
    document.addEventListener('pointerover', over, true);
    document.addEventListener('pointerout', out, true);
    document.addEventListener('focusin', focus, true);
    document.addEventListener('focusout', hide, true);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', key, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      clear();
      document.removeEventListener('pointerover', over, true);
      document.removeEventListener('pointerout', out, true);
      document.removeEventListener('focusin', focus, true);
      document.removeEventListener('focusout', hide, true);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  /* A tooltip whose element went away (the card opened, the list changed)
     goes with it — it must never be left floating over another screen. */
  useEffect(() => {
    if (!tip) return;
    const iv = window.setInterval(() => {
      if (!tip.el.isConnected || tip.el.offsetParent === null) { target.current = null; setTip(null); setPos(null); }
    }, 300);
    return () => window.clearInterval(iv);
  }, [tip]);

  /* Placed against the window once its size is known: above (or beside, or
     below), flipped when there is no room, and kept 8px inside the screen. */
  useLayoutEffect(() => {
    if (!tip || !box.current) { setPos(null); return; }
    if (!tip.el.isConnected) { setTip(null); return; }
    const r = tip.el.getBoundingClientRect();
    const b = box.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, gap = 8, pad = 8;
    let side = tip.side;
    if (side === 'right' && r.right + gap + b.width > vw - pad) side = 'top';
    if (side === 'top' && r.top - gap - b.height < pad) side = 'bottom';
    if (side === 'bottom' && r.bottom + gap + b.height > vh - pad) side = 'top';
    let x: number, y: number;
    if (side === 'right') { x = r.right + gap; y = r.top + r.height / 2 - b.height / 2; }
    else { x = r.left + r.width / 2 - b.width / 2; y = side === 'top' ? r.top - gap - b.height : r.bottom + gap; }
    x = Math.max(pad, Math.min(x, vw - pad - b.width));
    y = Math.max(pad, Math.min(y, vh - pad - b.height));
    setPos({ x, y, side });
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div
      ref={box}
      className={`tipx ${pos ? `on ${pos.side}` : ''}`}
      role="tooltip"
      style={pos ? { left: pos.x, top: pos.y } : { left: -9999, top: -9999 }}
    >
      {tip.head && <b>{tip.head}</b>}
      {tip.text && <span>{tip.text}</span>}
    </div>,
    document.body,
  );
}
